import { useEffect, useState } from "react";
import { X, ExternalLink, Loader2 } from "lucide-react";
import { getNotificationArticle } from "@/lib/api";

export default function ArticleModal({ item, onClose }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    setError("");
    setArticle(null);
    getNotificationArticle(item.url)
      .then((r) => setArticle(r.data))
      .catch(() => setError("Full article could not be fetched. Use the source link below."))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="article-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-black w-full max-w-3xl max-h-[88vh] overflow-y-auto"
        style={{ boxShadow: "8px 8px 0px 0px #0A0A0A" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
              <span className="font-mono-plex">{item.date}</span> · {item.category} · {item.source}
            </div>
            <h2 className="font-heading text-xl font-medium tracking-tight pr-8" data-testid="article-title">{item.title}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="article-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-zinc-500 py-12 text-center"><Loader2 size={16} className="inline animate-spin" /> Loading article…</div>
          ) : error ? (
            <div className="text-zinc-600 py-6 text-sm" data-testid="article-error">
              <p>{error}</p>
              <a href={item.url} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-3">
                Open source <ExternalLink size={12} />
              </a>
            </div>
          ) : article ? (
            <>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-zinc-800 leading-relaxed" data-testid="article-body">
                {article.body}
              </div>
              <div className="mt-6 pt-4 border-t border-zinc-200 text-xs text-zinc-500">
                Source: <a href={item.url} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  {item.url.length > 80 ? item.url.slice(0, 80) + "…" : item.url} <ExternalLink size={11} />
                </a>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
